import { where, orderBy, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { firebaseService } from './firebase.service';
import { authService } from './auth.service';
import { AdminUser, DashboardStats, User, Booking, Transaction, Payment, WithdrawalRequest, PaginationResult, Ticket, TicketReply, TicketStatus, Feedback, SystemSettings, StudentProfile, TutorProfile, ChatMessage, ChatRoom, RefundRequest, LiveSession, TriviaQuestion, EducationalShort, BrainTeaser, Notification, AnalyticsData } from '../types';

export const apiService = {
  // Dashboard
  getDashboardStats: async (period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<DashboardStats> => {
    const [users, bookings, withdrawals, activeSessions] = await Promise.all([
      firebaseService.getDocuments<User>('users'),
      firebaseService.getDocuments<Booking>('bookings'),
      firebaseService.getDocuments<WithdrawalRequest>('withdrawalRequests', [
        where('status', '==', 'pending'),
      ]),
      firebaseService.getDocuments<Booking>('bookings', [
        where('meetingStatus', 'in', ['inProgress', 'waitingForStartOTP', 'waitingForTutorJoin']),
      ]),
    ]);

    const now = new Date();
    let startDate = new Date();
    if (period === 'daily') {
      startDate.setHours(0, 0, 0, 0);
    } else if (period === 'weekly') {
      startDate.setDate(now.getDate() - 7);
    } else if (period === 'monthly') {
      startDate.setMonth(now.getMonth() - 1);
    }

    const filteredBookings = bookings.filter((b: any) => {
      const createdAt = firebaseService.convertTimestamp(b.createdAt);
      return createdAt >= startDate;
    });

    const paidBookings = filteredBookings.filter((b: any) => b.paymentStatus === 'paid');
    const totalRevenue = paidBookings.reduce((sum: number, b: any) => sum + (b.amount || 0), 0);

    const calculatePlatformFee = (totalAmount: number) => {
      const classCharges = totalAmount / 1.13;
      return classCharges * 0.10;
    };

    const totalCommission = paidBookings.reduce((sum: number, b: any) => {
      return sum + calculatePlatformFee(b.amount || 0);
    }, 0);

    return {
      totalUsers: users.length,
      students: users.filter((u) => u.role === 'student').length,
      tutors: users.filter((u) => u.role === 'tutor').length,
      activeBookingsToday: filteredBookings.filter(
        (b: any) => b.status === 'accepted' || b.status === 'pending'
      ).length,
      revenueToday: totalRevenue,
      platformCommissionToday: parseFloat(totalCommission.toFixed(2)),
      pendingWithdrawalRequests: withdrawals.length,
      activeSessionsNow: activeSessions.length,
    };
  },

  getRevenue: async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly') => {
    const bookings = await firebaseService.getDocuments<Booking>('bookings', [
      where('paymentStatus', '==', 'paid'),
    ]);

    const now = new Date();
    let startDate: Date;
    switch (period) {
      case 'daily':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'weekly':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'monthly':
        startDate = new Date(now.setMonth(now.getMonth() - 1));
        break;
      case 'yearly':
        startDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
    }

    const filteredBookings = bookings.filter((b: any) => {
      const createdAt = firebaseService.convertTimestamp(b.createdAt);
      return createdAt >= startDate;
    });

    const totalRevenue = filteredBookings.reduce((sum: number, b: any) => sum + (b.amount || 0), 0);

    // Calculate platform fees correctly: Class Charges = Total / 1.13, Platform Fee = Class Charges * 0.10, Transaction Fee = Class Charges * 0.03
    const calculateBreakdown = (totalAmount: number) => {
      const classCharges = totalAmount / 1.13;
      const platformFee = classCharges * 0.10;
      const transactionFee = classCharges * 0.03;
      return {
        classCharges: parseFloat(classCharges.toFixed(2)),
        platformFee: parseFloat(platformFee.toFixed(2)),
        transactionFee: parseFloat(transactionFee.toFixed(2)),
      };
    };

    let totalPlatformFee = 0;
    let totalTransactionFee = 0;
    let totalClassCharges = 0;

    filteredBookings.forEach((b: any) => {
      const breakdown = calculateBreakdown(b.amount || 0);
      totalPlatformFee += breakdown.platformFee;
      totalTransactionFee += breakdown.transactionFee;
      totalClassCharges += breakdown.classCharges;
    });

    return {
      period,
      totalRevenue,
      platformCommission: parseFloat(totalPlatformFee.toFixed(2)),
      transactionFees: parseFloat(totalTransactionFee.toFixed(2)),
      tutorEarnings: parseFloat(totalClassCharges.toFixed(2)),
      bookingCount: filteredBookings.length,
    };
  },

  getUserGrowth: async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly') => {
    const users = await firebaseService.getDocuments<User>('users');
    const now = new Date();
    let startDate: Date;
    switch (period) {
      case 'daily':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'weekly':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'monthly':
        startDate = new Date(now.setMonth(now.getMonth() - 1));
        break;
      case 'yearly':
        startDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
    }

    const filteredUsers = users.filter((u: any) => {
      const createdAt = firebaseService.convertTimestamp(u.createdAt);
      return createdAt >= startDate;
    });

    return {
      period,
      totalNewUsers: filteredUsers.length,
      newStudents: filteredUsers.filter((u) => u.role === 'student').length,
      newTutors: filteredUsers.filter((u) => u.role === 'tutor').length,
    };
  },

  // Users
  getUsers: async (filters: any = {}): Promise<PaginationResult<User>> => {
    const constraints: any[] = [];
    if (filters.role) {
      constraints.push(where('role', '==', filters.role));
    }
    if (filters.profileCompleted !== undefined) {
      constraints.push(where('profileCompleted', '==', filters.profileCompleted));
    }

    let result = await firebaseService.getDocumentsPaginated<User>(
      'users',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((user: User) => {
        const name = (user.studentProfile?.name || user.tutorProfile?.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const id = (user.id || '').toLowerCase();
        return name.includes(searchTerm) ||
          email.includes(searchTerm) ||
          phone.includes(searchTerm) ||
          id.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getStudents: async (filters: any = {}): Promise<PaginationResult<User>> => {
    const constraints = [where('role', '==', 'student')];
    if (filters.profileCompleted !== undefined) {
      constraints.push(where('profileCompleted', '==', filters.profileCompleted));
    }

    let result = await firebaseService.getDocumentsPaginated<User>(
      'users',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((user: User) => {
        const name = (user.studentProfile?.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const id = (user.id || '').toLowerCase();
        return name.includes(searchTerm) ||
          email.includes(searchTerm) ||
          phone.includes(searchTerm) ||
          id.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getTutors: async (filters: any = {}): Promise<PaginationResult<User>> => {
    const constraints = [where('role', '==', 'tutor')];
    if (filters.isVerified !== undefined) {
      constraints.push(where('tutorProfile.isVerified', '==', filters.isVerified));
    }

    let result = await firebaseService.getDocumentsPaginated<User>(
      'users',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((user: User) => {
        const name = (user.tutorProfile?.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const id = (user.id || '').toLowerCase();
        return name.includes(searchTerm) ||
          email.includes(searchTerm) ||
          phone.includes(searchTerm) ||
          id.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getUser: async (id: string): Promise<User> => {
    const user = await firebaseService.getDocument<User>('users', id);
    if (!user) throw new Error('User not found');
    return user;
  },

  updateUser: async (id: string, data: Partial<User>): Promise<User> => {
    await firebaseService.updateDocument('users', id, data);
    return apiService.getUser(id);
  },

  suspendUser: async (id: string): Promise<void> => {
    await firebaseService.updateDocument('users', id, { isActive: false });
  },

  activateUser: async (id: string): Promise<void> => {
    await firebaseService.updateDocument('users', id, { isActive: true });
  },

  verifyTutor: async (id: string): Promise<void> => {
    await firebaseService.updateDocument('users', id, {
      'tutorProfile.isVerified': true,
    });
  },

  unverifyTutor: async (id: string): Promise<void> => {
    await firebaseService.updateDocument('users', id, {
      'tutorProfile.isVerified': false,
    });
  },

  // Bookings
  getBookings: async (filters: any = {}): Promise<PaginationResult<Booking>> => {
    const constraints: any[] = [];
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.paymentStatus) {
      constraints.push(where('paymentStatus', '==', filters.paymentStatus));
    }
    if (filters.tutorId) {
      constraints.push(where('tutorId', '==', filters.tutorId));
    }
    if (filters.studentId) {
      constraints.push(where('studentId', '==', filters.studentId));
    }

    let result = await firebaseService.getDocumentsPaginated<Booking>(
      'bookings',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Debug: Log recurring bookings count
    const recurringCount = result.data.filter((b: Booking) => b.parentBookingId).length;
    if (recurringCount > 0) {
      console.log(`Found ${recurringCount} recurring bookings in results`);
    }

    // Client-side filtering for recurring bookings
    if (filters.recurringOnly === true) {
      result.data = result.data.filter((booking: Booking) => {
        return booking.parentBookingId != null && booking.parentBookingId !== '';
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
      console.log(`Filtered to ${result.data.length} recurring bookings`);
    }

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((booking: Booking) => {
        const id = (booking.id || '').toLowerCase();
        const bookingId = (booking.bookingId || '').toLowerCase();
        const tutorId = (booking.tutorId || '').toLowerCase();
        const studentId = (booking.studentId || '').toLowerCase();
        const tutorName = (booking.tutorName || '').toLowerCase();
        const studentName = (booking.studentName || '').toLowerCase();
        const subject = (booking.subject || '').toLowerCase();
        const status = (booking.status || '').toLowerCase();
        const paymentStatus = (booking.paymentStatus || '').toLowerCase();
        const parentBookingId = (booking.parentBookingId || '').toLowerCase();
        return id.includes(searchTerm) ||
          bookingId.includes(searchTerm) ||
          tutorId.includes(searchTerm) ||
          studentId.includes(searchTerm) ||
          tutorName.includes(searchTerm) ||
          studentName.includes(searchTerm) ||
          subject.includes(searchTerm) ||
          status.includes(searchTerm) ||
          paymentStatus.includes(searchTerm) ||
          parentBookingId.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getBooking: async (id: string): Promise<Booking> => {
    const booking = await firebaseService.getDocument<Booking>('bookings', id);
    if (!booking) throw new Error('Booking not found');
    return booking;
  },

  cancelBooking: async (id: string, reason?: string): Promise<void> => {
    await firebaseService.updateDocument('bookings', id, {
      status: 'cancelled',
      cancelledBy: 'admin',
      cancellationReason: reason || 'Cancelled by admin',
    });
  },

  // Financial
  getTransactions: async (filters: any = {}): Promise<PaginationResult<Transaction>> => {
    const constraints: any[] = [];
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.type) {
      constraints.push(where('type', '==', filters.type));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    let result = await firebaseService.getDocumentsPaginated<Transaction>(
      'transactions',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((tx: Transaction) => {
        const id = (tx.id || '').toLowerCase();
        const userId = (tx.userId || '').toLowerCase();
        const type = (tx.type || '').toLowerCase();
        const status = (tx.status || '').toLowerCase();
        const description = (tx.description || '').toLowerCase();
        return id.includes(searchTerm) ||
          userId.includes(searchTerm) ||
          type.includes(searchTerm) ||
          status.includes(searchTerm) ||
          description.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getPayments: async (filters: any = {}): Promise<PaginationResult<Payment>> => {
    const constraints: any[] = [];
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.method) {
      constraints.push(where('method', '==', filters.method));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    let result = await firebaseService.getDocumentsPaginated<Payment>(
      'payments',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((payment: Payment) => {
        const id = (payment.id || '').toLowerCase();
        const userId = (payment.userId || '').toLowerCase();
        const method = (payment.method || '').toLowerCase();
        const type = (payment.type || '').toLowerCase();
        const status = (payment.status || '').toLowerCase();
        return id.includes(searchTerm) ||
          userId.includes(searchTerm) ||
          method.includes(searchTerm) ||
          type.includes(searchTerm) ||
          status.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getWithdrawals: async (filters: any = {}): Promise<PaginationResult<WithdrawalRequest>> => {
    const constraints: any[] = [];
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.tutorId) {
      constraints.push(where('tutorId', '==', filters.tutorId));
    }

    let result = await firebaseService.getDocumentsPaginated<WithdrawalRequest>(
      'withdrawalRequests',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'requestedAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((withdrawal: WithdrawalRequest) => {
        const id = (withdrawal.id || '').toLowerCase();
        const tutorId = (withdrawal.tutorId || '').toLowerCase();
        const status = (withdrawal.status || '').toLowerCase();
        return id.includes(searchTerm) ||
          tutorId.includes(searchTerm) ||
          status.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  approveWithdrawal: async (id: string): Promise<void> => {
    await firebaseService.updateDocument('withdrawalRequests', id, {
      status: 'success',
      processedAt: new Date(),
    });
  },

  rejectWithdrawal: async (id: string, reason: string): Promise<void> => {
    await firebaseService.updateDocument('withdrawalRequests', id, {
      status: 'rejected',
      rejectionReason: reason,
      processedAt: new Date(),
    });
  },

  createWithdrawalRequest: async (data: any): Promise<string> => {
    return await firebaseService.addDocument('withdrawalRequests', data);
  },

  updateWithdrawalRequest: async (id: string, data: Partial<WithdrawalRequest>): Promise<void> => {
    await firebaseService.updateDocument('withdrawalRequests', id, data);
  },

  getRevenueBySubject: async () => {
    const bookings = await firebaseService.getDocuments<Booking>('bookings', [
      where('paymentStatus', '==', 'paid'),
    ]);

    const calculateBreakdown = (totalAmount: number) => {
      const classCharges = totalAmount / 1.13;
      const platformFee = classCharges * 0.10;
      const transactionFee = classCharges * 0.03;
      return {
        classCharges: parseFloat(classCharges.toFixed(2)),
        platformFee: parseFloat(platformFee.toFixed(2)),
        transactionFee: parseFloat(transactionFee.toFixed(2)),
      };
    };

    const revenueBySubject: Record<string, { total: number; platformFee: number; classCharges: number; transactionFee: number }> = {};
    bookings.forEach((booking: any) => {
      const subject = booking.subject || 'Unknown';
      const breakdown = calculateBreakdown(booking.amount || 0);
      if (!revenueBySubject[subject]) {
        revenueBySubject[subject] = { total: 0, platformFee: 0, classCharges: 0, transactionFee: 0 };
      }
      revenueBySubject[subject].total += booking.amount || 0;
      revenueBySubject[subject].platformFee += breakdown.platformFee;
      revenueBySubject[subject].classCharges += breakdown.classCharges;
      revenueBySubject[subject].transactionFee += breakdown.transactionFee;
    });

    return Object.entries(revenueBySubject).map(([subject, data]) => ({
      subject,
      revenue: data.total,
      platformCommission: data.platformFee,
      tutorEarnings: data.classCharges,
      transactionFees: data.transactionFee,
    }));
  },

  // Time-series data for charts
  getRevenueTrend: async (period: 'daily' | 'weekly' | 'monthly' = 'monthly', days: number = 30) => {
    const bookings = await firebaseService.getDocuments<Booking>('bookings', [
      where('paymentStatus', '==', 'paid'),
    ]);

    const now = new Date();
    const startDate = new Date(now);

    if (period === 'daily') {
      startDate.setDate(now.getDate() - days);
    } else if (period === 'weekly') {
      startDate.setDate(now.getDate() - (days * 7));
    } else {
      startDate.setMonth(now.getMonth() - days);
    }

    const filteredBookings = bookings.filter((b: any) => {
      const createdAt = firebaseService.convertTimestamp(b.createdAt);
      return createdAt >= startDate;
    });

    const calculateBreakdown = (totalAmount: number) => {
      const classCharges = totalAmount / 1.13;
      const platformFee = classCharges * 0.10;
      const transactionFee = classCharges * 0.03;
      return {
        classCharges: parseFloat(classCharges.toFixed(2)),
        platformFee: parseFloat(platformFee.toFixed(2)),
        transactionFee: parseFloat(transactionFee.toFixed(2)),
      };
    };

    const dataMap: Record<string, { revenue: number; platformFee: number; classCharges: number; transactionFee: number; bookings: number }> = {};

    filteredBookings.forEach((booking: any) => {
      const createdAt = firebaseService.convertTimestamp(booking.createdAt);
      let key: string;

      if (period === 'daily') {
        key = createdAt.toISOString().split('T')[0];
      } else if (period === 'weekly') {
        const weekStart = new Date(createdAt);
        weekStart.setDate(createdAt.getDate() - createdAt.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!dataMap[key]) {
        dataMap[key] = { revenue: 0, platformFee: 0, classCharges: 0, transactionFee: 0, bookings: 0 };
      }
      const breakdown = calculateBreakdown(booking.amount || 0);
      dataMap[key].revenue += booking.amount || 0;
      dataMap[key].platformFee += breakdown.platformFee;
      dataMap[key].classCharges += breakdown.classCharges;
      dataMap[key].transactionFee += breakdown.transactionFee;
      dataMap[key].bookings += 1;
    });

    return Object.entries(dataMap)
      .map(([date, data]) => ({
        date,
        revenue: data.revenue,
        platformCommission: data.platformFee,
        tutorEarnings: data.classCharges,
        transactionFees: data.transactionFee,
        bookings: data.bookings,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  },

  getUserGrowthTrend: async (period: 'daily' | 'weekly' | 'monthly' = 'monthly', days: number = 30) => {
    const users = await firebaseService.getDocuments<User>('users');

    const now = new Date();
    const startDate = new Date(now);

    if (period === 'daily') {
      startDate.setDate(now.getDate() - days);
    } else if (period === 'weekly') {
      startDate.setDate(now.getDate() - (days * 7));
    } else {
      startDate.setMonth(now.getMonth() - days);
    }

    const filteredUsers = users.filter((u: any) => {
      const createdAt = firebaseService.convertTimestamp(u.createdAt);
      return createdAt >= startDate;
    });

    const dataMap: Record<string, { students: number; tutors: number; total: number }> = {};

    filteredUsers.forEach((user: any) => {
      const createdAt = firebaseService.convertTimestamp(user.createdAt);
      let key: string;

      if (period === 'daily') {
        key = createdAt.toISOString().split('T')[0];
      } else if (period === 'weekly') {
        const weekStart = new Date(createdAt);
        weekStart.setDate(createdAt.getDate() - createdAt.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!dataMap[key]) {
        dataMap[key] = { students: 0, tutors: 0, total: 0 };
      }
      dataMap[key].total += 1;
      if (user.role === 'student') {
        dataMap[key].students += 1;
      } else if (user.role === 'tutor') {
        dataMap[key].tutors += 1;
      }
    });

    return Object.entries(dataMap)
      .map(([date, data]) => ({
        date,
        total: data.total,
        students: data.students,
        tutors: data.tutors,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  },

  getBookingStatusDistribution: async () => {
    const bookings = await firebaseService.getDocuments<Booking>('bookings');

    const distribution: Record<string, number> = {
      pending: 0,
      accepted: 0,
      rejected: 0,
      completed: 0,
      cancelled: 0,
    };

    bookings.forEach((booking: any) => {
      const status = (booking.status || 'pending').toLowerCase();
      distribution[status] = (distribution[status] || 0) + 1;
    });

    return Object.entries(distribution)
      .map(([status, count]) => ({
        status: status.charAt(0).toUpperCase() + status.slice(1),
        count,
      }))
      .filter(item => item.count > 0);
  },

  // Tickets
  getTickets: async (filters: any = {}): Promise<PaginationResult<Ticket>> => {
    const constraints: any[] = [];
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }

    let result = await firebaseService.getDocumentsPaginated<Ticket>(
      'tickets',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Client-side search filtering
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((ticket: Ticket) => {
        const ticketNumber = (ticket.ticketNumber || '').toLowerCase();
        const subject = (ticket.subject || '').toLowerCase();
        const description = (ticket.description || '').toLowerCase();
        const userName = (ticket.userName || '').toLowerCase();
        const userEmail = (ticket.userEmail || '').toLowerCase();
        const status = (ticket.status || '').toLowerCase();
        return ticketNumber.includes(searchTerm) ||
          subject.includes(searchTerm) ||
          description.includes(searchTerm) ||
          userName.includes(searchTerm) ||
          userEmail.includes(searchTerm) ||
          status.includes(searchTerm);
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const ticket = await firebaseService.getDocument<Ticket>('tickets', id);
    if (!ticket) throw new Error('Ticket not found');
    return ticket;
  },

  updateTicketStatus: async (id: string, status: TicketStatus): Promise<void> => {
    const updateData: any = {
      status,
      updatedAt: new Date(),
    };

    if (status === 'resolved' || status === 'closed') {
      updateData.resolvedAt = new Date();
    } else {
      updateData.resolvedAt = null;
    }

    await firebaseService.updateDocument('tickets', id, updateData);
  },

  getTicketReplies: async (ticketId: string): Promise<TicketReply[]> => {
    const replies = await firebaseService.getSubDocuments<TicketReply>(
      'tickets',
      ticketId,
      'replies',
      [orderBy('timestamp', 'asc')]
    );

    return replies;
  },

  addTicketReply: async (reply: Omit<TicketReply, 'id'>): Promise<string> => {
    const user = authService.getCurrentUser();
    const replyData = {
      ticketId: reply.ticketId,
      senderId: user?.id || 'admin',
      senderName: user?.name || 'Admin',
      message: reply.message,
      timestamp: new Date(),
      isAdmin: true,
    };

    const replyId = await firebaseService.addSubDocument('tickets', reply.ticketId, 'replies', replyData);

    // Update ticket's updatedAt timestamp
    await firebaseService.updateDocument('tickets', reply.ticketId, {
      updatedAt: new Date(),
    });

    return replyId;
  },

  // Feedbacks
  getFeedbacks: async (filters: any = {}): Promise<PaginationResult<Feedback>> => {
    const constraints: any[] = [];
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.studentId) {
      constraints.push(where('studentId', '==', filters.studentId));
    }
    if (filters.tutorId) {
      constraints.push(where('tutorId', '==', filters.tutorId));
    }
    if (filters.rating) {
      constraints.push(where('rating', '==', filters.rating));
    }
    if (filters.tutorRating) {
      constraints.push(where('tutorRating', '==', filters.tutorRating));
    }

    let result = await firebaseService.getDocumentsPaginated<Feedback>(
      'feedbacks',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    // Enrich feedbacks with student and tutor names
    const enrichedFeedbacks = await Promise.all(
      result.data.map(async (feedback: Feedback) => {
        try {
          const studentId = feedback.studentId || feedback.userId;
          const [student, tutor] = await Promise.all([
            studentId ? firebaseService.getDocument<User>('users', studentId).catch(() => null) : Promise.resolve(null),
            firebaseService.getDocument<User>('users', feedback.tutorId).catch(() => null),
          ]);

          return {
            ...feedback,
            studentName: student?.studentProfile?.name || student?.tutorProfile?.name || student?.email || 'Unknown Student',
            userName: student?.studentProfile?.name || student?.tutorProfile?.name || student?.email || 'Unknown User',
            tutorName: tutor?.tutorProfile?.name || tutor?.studentProfile?.name || tutor?.email || 'Unknown Tutor',
          };
        } catch (error) {
          console.error('Error enriching feedback:', error);
          return feedback;
        }
      })
    );

    // Client-side search filtering
    let filteredFeedbacks = enrichedFeedbacks;
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filteredFeedbacks = enrichedFeedbacks.filter((feedback: Feedback) => {
        const id = (feedback.id || '').toLowerCase();
        const userId = (feedback.userId || '').toLowerCase();
        const studentId = (feedback.studentId || '').toLowerCase();
        const tutorId = (feedback.tutorId || '').toLowerCase();
        const studentName = ((feedback as any).studentName || (feedback as any).userName || '').toLowerCase();
        const tutorName = ((feedback as any).tutorName || '').toLowerCase();
        const comment = (feedback.comment || '').toLowerCase();
        const feedbackText = (feedback.feedbackText || '').toLowerCase();
        const subject = (feedback.subject || '').toLowerCase();
        const rating = String(feedback.rating || '');
        const tutorRating = String(feedback.tutorRating || '');
        const platformRating = String(feedback.platformRating || '');

        return id.includes(searchTerm) ||
          userId.includes(searchTerm) ||
          studentId.includes(searchTerm) ||
          tutorId.includes(searchTerm) ||
          studentName.includes(searchTerm) ||
          tutorName.includes(searchTerm) ||
          comment.includes(searchTerm) ||
          feedbackText.includes(searchTerm) ||
          subject.includes(searchTerm) ||
          rating.includes(searchTerm) ||
          tutorRating.includes(searchTerm) ||
          platformRating.includes(searchTerm);
      });
      result.pagination.total = filteredFeedbacks.length;
      result.pagination.totalPages = Math.ceil(filteredFeedbacks.length / (filters.limit || 10));
    }

    result.data = filteredFeedbacks;
    return result;
  },

  getFeedback: async (id: string): Promise<Feedback> => {
    const feedback = await firebaseService.getDocument<Feedback>('feedbacks', id);
    if (!feedback) throw new Error('Feedback not found');

    // Enrich with student and tutor names
    try {
      const studentId = feedback.studentId || feedback.userId;
      const [student, tutor] = await Promise.all([
        studentId ? firebaseService.getDocument<User>('users', studentId).catch(() => null) : Promise.resolve(null),
        firebaseService.getDocument<User>('users', feedback.tutorId).catch(() => null),
      ]);

      return {
        ...feedback,
        studentName: student?.studentProfile?.name || student?.tutorProfile?.name || student?.email || 'Unknown Student',
        userName: student?.studentProfile?.name || student?.tutorProfile?.name || student?.email || 'Unknown User',
        tutorName: tutor?.tutorProfile?.name || tutor?.studentProfile?.name || tutor?.email || 'Unknown Tutor',
      };
    } catch (error) {
      console.error('Error enriching feedback:', error);
      return feedback;
    }
  },

  deleteFeedback: async (id: string): Promise<void> => {
    await firebaseService.deleteDocument('feedbacks', id);
  },

  // System Settings
  getSystemSettings: async (): Promise<SystemSettings> => {
    const maintenanceRef = doc(db, 'config', 'under_maintenance');
    const bookingsRef = doc(db, 'config', 'pause_bookings');
    const zoneRef = doc(db, 'config', 'zone');

    const [maintenanceSnap, bookingsSnap, zoneSnap] = await Promise.all([
      getDoc(maintenanceRef),
      getDoc(bookingsRef),
      getDoc(zoneRef),
    ]);

    const settings: SystemSettings = {
      maintenanceMode: maintenanceSnap.exists() ? maintenanceSnap.data().under_maintenance : false,
      bookingsPaused: bookingsSnap.exists() ? bookingsSnap.data().pause_bookings : false,
      learnMode: zoneSnap.exists() ? zoneSnap.data().learn : false,
      updatedAt: serverTimestamp(), // Dashboard uses local time if not found, but we'll return a placeholder
    };

    return settings;
  },

  updateSystemSettings: async (settings: Partial<SystemSettings>): Promise<void> => {
    const batch = [];

    if (settings.maintenanceMode !== undefined) {
      batch.push(setDoc(doc(db, 'config', 'under_maintenance'), {
        under_maintenance: settings.maintenanceMode,
        updatedAt: serverTimestamp()
      }, { merge: true }));
    }

    if (settings.bookingsPaused !== undefined) {
      batch.push(setDoc(doc(db, 'config', 'pause_bookings'), {
        pause_bookings: settings.bookingsPaused,
        updatedAt: serverTimestamp()
      }, { merge: true }));
    }

    if (settings.learnMode !== undefined) {
      batch.push(setDoc(doc(db, 'config', 'zone'), {
        learn: settings.learnMode,
        updatedAt: serverTimestamp()
      }, { merge: true }));
    }

    await Promise.all(batch);
  },

  pauseTutorWallet: async (id: string, paused: boolean): Promise<void> => {
    await firebaseService.updateDocument('users', id, { 'wallet.isWalletPaused': paused });
  },

  updateUserProfile: async (id: string, role: 'student' | 'tutor', profileData: Partial<StudentProfile | TutorProfile>): Promise<User> => {
    const profileField = role === 'student' ? 'studentProfile' : 'tutorProfile';
    const updateData: any = {};

    // Update nested profile fields
    Object.keys(profileData).forEach(key => {
      const value = (profileData as any)[key];
      if (value !== undefined) {
        updateData[`${profileField}.${key}`] = value;
      }
    });

    await firebaseService.updateDocument('users', id, updateData);
    return apiService.getUser(id);
  },

  updateWalletBalance: async (
    id: string,
    newBalance: number,
    operation: 'add' | 'subtract' | 'set',
    amount: number,
    description: string
  ): Promise<void> => {
    // Get current user to check wallet structure
    const user = await apiService.getUser(id);

    // Update wallet balance (ensure wallet object exists)
    const updateData: any = {
      'wallet.balance': newBalance,
    };

    // If wallet doesn't exist, initialize it
    if (!user.wallet) {
      updateData['wallet.currency'] = 'INR';
      updateData['wallet.isWalletPaused'] = false;
    }

    await firebaseService.updateDocument('users', id, updateData);

    // Create transaction record
    const transactionData = {
      userId: id,
      type: operation === 'add' ? 'credit' : 'debit' as 'credit' | 'debit',
      amount: amount,
      description: description,
      status: 'completed' as const,
    };

    await firebaseService.addDocument('transactions', transactionData);
  },

  // Chat Moderation
  getChatRooms: async (filters: any = {}): Promise<PaginationResult<ChatRoom>> => {
    let result = await firebaseService.getDocumentsPaginated<ChatRoom>(
      'chats',
      filters.page || 1,
      filters.limit || 10,
      [],
      'updatedAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((room: ChatRoom) => {
        return (
          (room.studentName || '').toLowerCase().includes(searchTerm) ||
          (room.tutorName || '').toLowerCase().includes(searchTerm) ||
          (room.studentId || '').toLowerCase().includes(searchTerm) ||
          (room.tutorId || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getChatMessages: async (chatId: string): Promise<ChatMessage[]> => {
    const messages = await firebaseService.getSubDocuments<ChatMessage>(
      'chats',
      chatId,
      'messages',
      [orderBy('timestamp', 'desc')]
    );
    return messages;
  },

  deleteChatMessage: async (chatId: string, messageId: string): Promise<void> => {
    const user = authService.getCurrentUser();
    await firebaseService.updateDocument('chats', chatId, {
      [`messages.${messageId}.isDeleted`]: true,
      [`messages.${messageId}.deletedAt`]: new Date(),
      [`messages.${messageId}.deletedBy`]: user?.id || 'admin',
    });
  },

  // Refund Management
  getRefundRequests: async (filters: any = {}): Promise<PaginationResult<RefundRequest>> => {
    const constraints: any[] = [];
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    let result = await firebaseService.getDocumentsPaginated<RefundRequest>(
      'refundRequests',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'requestedAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((refund: RefundRequest) => {
        return (
          (refund.userName || '').toLowerCase().includes(searchTerm) ||
          (refund.bookingId || '').toLowerCase().includes(searchTerm) ||
          (refund.id || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  approveRefund: async (id: string): Promise<void> => {
    const user = authService.getCurrentUser();
    await firebaseService.updateDocument('refundRequests', id, {
      status: 'approved',
      processedAt: new Date(),
      processedBy: user?.id || 'admin',
    });
  },

  rejectRefund: async (id: string, reason: string): Promise<void> => {
    const user = authService.getCurrentUser();
    await firebaseService.updateDocument('refundRequests', id, {
      status: 'rejected',
      rejectionReason: reason,
      processedAt: new Date(),
      processedBy: user?.id || 'admin',
    });
  },

  // Live Session Monitoring
  getLiveSessions: async (): Promise<LiveSession[]> => {
    const bookings = await firebaseService.getDocuments<Booking>('bookings', [
      where('meetingStatus', 'in', ['inProgress', 'waitingForStartOTP', 'waitingForTutorJoin']),
    ]);

    return bookings.map((booking: any) => ({
      id: booking.id,
      bookingId: booking.id,
      jitsiRoomName: booking.jitsiRoomName || '',
      studentId: booking.studentId,
      studentName: booking.studentName,
      tutorId: booking.tutorId,
      tutorName: booking.tutorName,
      subject: booking.subject,
      scheduledStartTime: booking.date + ' ' + booking.time,
      scheduledEndTime: booking.classEndTime || '',
      actualStartTime: booking.classStartTime,
      status: booking.meetingStatus || 'scheduled',
      studentJoined: booking.studentJoined || false,
      tutorJoined: booking.tutorJoined || false,
      meetingUrl: booking.jitsiRoomName ? `https://tutorapp.duckdns.org/${booking.jitsiRoomName}` : undefined,
    }));
  },

  getAllSessions: async (filters: any = {}): Promise<PaginationResult<LiveSession>> => {
    const constraints: any[] = [];
    if (filters.status) {
      constraints.push(where('meetingStatus', '==', filters.status));
    }

    const bookings = await firebaseService.getDocuments<Booking>('bookings', constraints);

    const sessions: LiveSession[] = bookings.map((booking: any) => ({
      id: booking.id,
      bookingId: booking.id,
      jitsiRoomName: booking.jitsiRoomName || '',
      studentId: booking.studentId,
      studentName: booking.studentName,
      tutorId: booking.tutorId,
      tutorName: booking.tutorName,
      subject: booking.subject,
      scheduledStartTime: booking.date + ' ' + booking.time,
      scheduledEndTime: booking.classEndTime || '',
      actualStartTime: booking.classStartTime,
      actualEndTime: booking.actualEndTime,
      status: booking.meetingStatus || 'scheduled',
      studentJoined: booking.studentJoined || false,
      tutorJoined: booking.tutorJoined || false,
      duration: booking.duration,
      meetingUrl: booking.jitsiRoomName ? `https://tutorapp.duckdns.org/${booking.jitsiRoomName}` : undefined,
    }));

    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedSessions = sessions.slice(startIndex, endIndex);

    return {
      data: paginatedSessions,
      pagination: {
        page,
        limit,
        total: sessions.length,
        totalPages: Math.ceil(sessions.length / limit),
      },
    };
  },

  // Content Management
  getTriviaQuestions: async (filters: any = {}): Promise<PaginationResult<TriviaQuestion>> => {
    const constraints: any[] = [];
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }

    let result = await firebaseService.getDocumentsPaginated<TriviaQuestion>(
      'triviaQuestions',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((q: TriviaQuestion) => {
        return (
          (q.question || '').toLowerCase().includes(searchTerm) ||
          (q.category || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getEducationalShorts: async (filters: any = {}): Promise<PaginationResult<EducationalShort>> => {
    const constraints: any[] = [];
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }

    let result = await firebaseService.getDocumentsPaginated<EducationalShort>(
      'educationalShorts',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((s: EducationalShort) => {
        return (
          (s.title || '').toLowerCase().includes(searchTerm) ||
          (s.description || '').toLowerCase().includes(searchTerm) ||
          (s.category || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  getBrainTeasers: async (filters: any = {}): Promise<PaginationResult<BrainTeaser>> => {
    const constraints: any[] = [];
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }

    let result = await firebaseService.getDocumentsPaginated<BrainTeaser>(
      'brainTeasers',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((b: BrainTeaser) => {
        return (
          (b.title || '').toLowerCase().includes(searchTerm) ||
          (b.question || '').toLowerCase().includes(searchTerm) ||
          (b.category || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  updateContentStatus: async (collectionName: string, id: string, isActive: boolean): Promise<void> => {
    await firebaseService.updateDocument(collectionName, id, { isActive });
  },

  deleteContent: async (collectionName: string, id: string): Promise<void> => {
    await firebaseService.deleteDocument(collectionName, id);
  },

  // Notification Management
  getNotifications: async (filters: any = {}): Promise<PaginationResult<Notification>> => {
    const constraints: any[] = [];
    if (filters.type) {
      constraints.push(where('type', '==', filters.type));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.userRole) {
      constraints.push(where('userRole', '==', filters.userRole));
    }

    let result = await firebaseService.getDocumentsPaginated<Notification>(
      'notifications',
      filters.page || 1,
      filters.limit || 10,
      constraints,
      'createdAt',
      'desc'
    );

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result.data = result.data.filter((n: Notification) => {
        return (
          (n.title || '').toLowerCase().includes(searchTerm) ||
          (n.message || '').toLowerCase().includes(searchTerm) ||
          (n.body || '').toLowerCase().includes(searchTerm)
        );
      });
      result.pagination.total = result.data.length;
      result.pagination.totalPages = Math.ceil(result.data.length / (filters.limit || 10));
    }

    return result;
  },

  sendNotification: async (notification: any): Promise<string> => {
    // Cloud Function expects specific format:
    // - targetAudience: "all_students" | "all_tutors" | "specific_users"
    // - body: notification body (not "message")
    // - title: notification title
    // - priority: priority level
    // - sent: boolean (false initially, Cloud Function will set to true after sending)
    // - scheduleTime: optional (for scheduled notifications)
    // - specificUsers: array of user IDs (if targetAudience is "specific_users")
    const notificationData: any = {
      title: notification.title,
      body: notification.body || notification.message, // Support both 'body' and 'message'
      targetAudience: notification.targetAudience ||
        (notification.userRole === 'student' ? 'all_students' :
          notification.userRole === 'tutor' ? 'all_tutors' : 'all_students'),
      priority: notification.priority || 'normal',
      sent: notification.sent !== undefined ? notification.sent : false,
      createdAt: new Date(),
    };

    // Add optional fields if provided
    if (notification.scheduleTime) {
      notificationData.scheduleTime = notification.scheduleTime;
    }
    if (notification.specificUsers && Array.isArray(notification.specificUsers)) {
      notificationData.specificUsers = notification.specificUsers;
      notificationData.targetAudience = 'specific_users';
    }

    // Keep admin tracking fields if provided
    if (notification.type) notificationData.type = notification.type;
    if (notification.userRole) notificationData.userRole = notification.userRole;
    if (notification.status) notificationData.status = notification.status;

    return await firebaseService.addDocument('notifications', notificationData);
  },

  // Analytics
  getAnalytics: async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<AnalyticsData> => {
    const [users, bookings, feedbacks, chats] = await Promise.all([
      firebaseService.getDocuments<User>('users'),
      firebaseService.getDocuments<Booking>('bookings'),
      firebaseService.getDocuments<Feedback>('feedbacks'),
      firebaseService.getDocuments<ChatRoom>('chats'),
    ]);

    const now = new Date();
    let startDate = new Date();
    switch (period) {
      case 'daily':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'weekly':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'monthly':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'yearly':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    const filteredUsers = users.filter((u: any) => {
      const createdAt = firebaseService.convertTimestamp(u.createdAt);
      return createdAt >= startDate;
    });

    const filteredBookings = bookings.filter((b: any) => {
      const createdAt = firebaseService.convertTimestamp(b.createdAt);
      return createdAt >= startDate;
    });

    const paidBookings = filteredBookings.filter((b: any) => b.paymentStatus === 'paid');
    const totalRevenue = paidBookings.reduce((sum: number, b: any) => sum + (b.amount || 0), 0);
    const calculatePlatformFee = (totalAmount: number) => {
      const classCharges = totalAmount / 1.13;
      return classCharges * 0.10;
    };
    const platformCommission = paidBookings.reduce((sum: number, b: any) => {
      return sum + calculatePlatformFee(b.amount || 0);
    }, 0);

    const tutorEarnings = paidBookings.reduce((sum: number, b: any) => {
      const classCharges = b.amount / 1.13;
      return sum + classCharges;
    }, 0);

    const ratings = feedbacks.map((f: any) => f.tutorRating || f.rating || 0).filter((r: number) => r > 0);
    const averageRating = ratings.length > 0 ? ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length : 0;

    const activeSessions = bookings.filter((b: any) =>
      ['inProgress', 'waitingForStartOTP', 'waitingForTutorJoin'].includes(b.meetingStatus)
    );

    // Generate trend data
    const trendPeriod = period === 'yearly' ? 'monthly' : period;
    const userGrowthPromise = apiService.getUserGrowthTrend(trendPeriod as 'daily' | 'weekly' | 'monthly', period === 'daily' ? 30 : period === 'weekly' ? 12 : 12);
    const revenueTrendPromise = apiService.getRevenueTrend(trendPeriod as 'daily' | 'weekly' | 'monthly', period === 'daily' ? 30 : period === 'weekly' ? 12 : 12);
    const [userGrowthData, revenueTrendData] = await Promise.all([userGrowthPromise, revenueTrendPromise]);

    const bookingTrend = filteredBookings.reduce((acc: any, booking: any) => {
      const date = firebaseService.convertTimestamp(booking.createdAt);
      const key = period === 'daily'
        ? date.toISOString().split('T')[0]
        : period === 'weekly'
          ? `${date.getFullYear()}-W${Math.ceil(date.getDate() / 7)}`
          : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const subjectPopularity = filteredBookings.reduce((acc: any, booking: any) => {
      const subject = booking.subject || 'Unknown';
      if (!acc[subject]) {
        acc[subject] = { count: 0, revenue: 0 };
      }
      acc[subject].count += 1;
      if (booking.paymentStatus === 'paid') {
        acc[subject].revenue += booking.amount || 0;
      }
      return acc;
    }, {});

    const topTutors = filteredBookings.reduce((acc: any, booking: any) => {
      const tutorId = booking.tutorId;
      if (!acc[tutorId]) {
        acc[tutorId] = { tutorId, tutorName: booking.tutorName, bookings: 0, revenue: 0, ratings: [] };
      }
      acc[tutorId].bookings += 1;
      if (booking.paymentStatus === 'paid') {
        acc[tutorId].revenue += booking.amount || 0;
      }
      return acc;
    }, {});

    const topStudents = filteredBookings.reduce((acc: any, booking: any) => {
      const studentId = booking.studentId;
      if (!acc[studentId]) {
        acc[studentId] = { studentId, studentName: booking.studentName, bookings: 0, spent: 0 };
      }
      acc[studentId].bookings += 1;
      if (booking.paymentStatus === 'paid') {
        acc[studentId].spent += booking.amount || 0;
      }
      return acc;
    }, {});

    return {
      period,
      totalUsers: users.length,
      activeUsers: users.filter((u: any) => u.isActive !== false).length,
      newUsers: filteredUsers.length,
      totalBookings: filteredBookings.length,
      completedBookings: filteredBookings.filter((b: any) => b.status === 'completed').length,
      cancelledBookings: filteredBookings.filter((b: any) => b.status === 'cancelled').length,
      totalRevenue,
      platformCommission: parseFloat(platformCommission.toFixed(2)),
      tutorEarnings: parseFloat(tutorEarnings.toFixed(2)),
      averageBookingValue: paidBookings.length > 0 ? totalRevenue / paidBookings.length : 0,
      averageRating: parseFloat(averageRating.toFixed(2)),
      totalFeedbacks: feedbacks.length,
      activeSessions: activeSessions.length,
      chatMessages: chats.length,
      contentViews: 0, // Would need separate tracking
      userGrowth: userGrowthData.map((d: any) => ({ date: d.date, count: d.total })),
      revenueTrend: revenueTrendData.map((d: any) => ({ date: d.date, revenue: d.revenue })),
      bookingTrend: Object.entries(bookingTrend).map(([date, count]) => ({ date, count: count as number })),
      subjectPopularity: Object.entries(subjectPopularity).map(([subject, data]: [string, any]) => ({
        subject,
        count: data.count,
        revenue: data.revenue,
      })),
      topTutors: Object.values(topTutors)
        .map((t: any) => ({
          ...t,
          rating: 0, // Would need to calculate from feedbacks
        }))
        .sort((a: any, b: any) => b.revenue - a.revenue)
        .slice(0, 10),
      topStudents: (Object.values(topStudents) as any[])
        .sort((a: any, b: any) => b.spent - a.spent)
        .slice(0, 10),
    };
  },

  // Export Data
  exportData: async (type: 'users' | 'bookings' | 'transactions' | 'feedbacks', format: 'csv' | 'json' = 'csv'): Promise<string> => {
    let data: any[] = [];
    let headers: string[] = [];

    switch (type) {
      case 'users':
        const users = await firebaseService.getDocuments<User>('users');
        data = users.map((u: User) => ({
          id: u.id,
          email: u.email,
          phone: u.phone,
          role: u.role,
          name: u.studentProfile?.name || u.tutorProfile?.name || 'N/A',
          isActive: u.isActive !== false,
          createdAt: firebaseService.convertTimestamp(u.createdAt).toISOString(),
        }));
        headers = ['ID', 'Email', 'Phone', 'Role', 'Name', 'Is Active', 'Created At'];
        break;
      case 'bookings':
        const bookings = await firebaseService.getDocuments<Booking>('bookings');
        data = bookings.map((b: Booking) => ({
          id: b.id,
          bookingId: b.bookingId,
          studentName: b.studentName,
          tutorName: b.tutorName,
          subject: b.subject,
          date: b.date,
          time: b.time,
          amount: b.amount,
          status: b.status,
          paymentStatus: b.paymentStatus,
          createdAt: firebaseService.convertTimestamp(b.createdAt).toISOString(),
        }));
        headers = ['ID', 'Booking ID', 'Student', 'Tutor', 'Subject', 'Date', 'Time', 'Amount', 'Status', 'Payment Status', 'Created At'];
        break;
      case 'transactions':
        const transactions = await firebaseService.getDocuments<Transaction>('transactions');
        data = transactions.map((t: Transaction) => ({
          id: t.id,
          userId: t.userId,
          type: t.type,
          amount: t.amount,
          description: t.description,
          status: t.status,
          createdAt: firebaseService.convertTimestamp(t.createdAt).toISOString(),
        }));
        headers = ['ID', 'User ID', 'Type', 'Amount', 'Description', 'Status', 'Created At'];
        break;
      case 'feedbacks':
        const feedbacks = await firebaseService.getDocuments<Feedback>('feedbacks');
        data = feedbacks.map((f: Feedback) => ({
          id: f.id,
          studentId: f.studentId || f.userId,
          tutorId: f.tutorId,
          rating: f.tutorRating || f.rating,
          comment: f.feedbackText || f.comment,
          createdAt: firebaseService.convertTimestamp(f.createdAt).toISOString(),
        }));
        headers = ['ID', 'Student ID', 'Tutor ID', 'Rating', 'Comment', 'Created At'];
        break;
    }

    if (format === 'csv') {
      const csvRows = [headers.join(',')];
      data.forEach((row) => {
        const values = headers.map((header) => {
          const key = header.toLowerCase().replace(/\s+/g, '');
          const value = row[key] || row[header] || '';
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });
      return csvRows.join('\n');
    } else {
      return JSON.stringify(data, null, 2);
    }
  },
  // Admin Management
  getAdmins: async (): Promise<AdminUser[]> => {
    return firebaseService.getDocuments<AdminUser>('adminUsers');
  },

  deleteAdmin: async (id: string): Promise<void> => {
    await firebaseService.deleteDocument('adminUsers', id);
    // Also delete from users collection if needed, though this might be risky
    await firebaseService.deleteDocument('users', id);
  },
};
